import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CreditCard, KeyRound, Lock } from 'lucide-react';
import { connectGcp, prepareGcp, setKubernetesAccess, testGcpStateBackend } from '../lib/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Alert } from './ui/alert';
import { Toggle } from './ui/toggle';

/**
 * Guided GCP connect (Stage 4) — the keyless twin of the AWS role modal. Fetches a
 * `gcloud` setup script that builds a Workload Identity pool (trusting Lizrd's AWS
 * identity) and a read-only viewer service account, then completes the connection
 * with the resulting resource identifiers. No key is ever created or stored.
 */
export function GcpSetupModal({
  envId,
  onClose,
  onConnected,
}: {
  envId: string;
  onClose: () => void;
  onConnected: () => void;
}) {
  const [projectId, setProjectId] = useState('');
  const [serviceAccountEmail, setServiceAccountEmail] = useState('');
  const [workloadIdentityProvider, setWorkloadIdentityProvider] = useState('');
  const [regions, setRegions] = useState('');
  const [billingExportTable, setBillingExportTable] = useState('');
  const [stateBucket, setStateBucket] = useState('');
  const [stateObject, setStateObject] = useState('');
  const [includeK8s, setIncludeK8s] = useState(true);
  const [k8sAutoProvision, setK8sAutoProvision] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const prep = useQuery({
    queryKey: ['gcp-prepare', envId],
    queryFn: () => prepareGcp(envId),
    staleTime: Infinity,
    retry: false,
  });

  const connect = useMutation({
    mutationFn: () =>
      connectGcp(envId, {
        projectId: projectId.trim(),
        serviceAccountEmail: serviceAccountEmail.trim(),
        workloadIdentityProvider: workloadIdentityProvider.trim(),
        externalId: prep.data!.externalId,
        regions: regions.trim() ? regions.split(',').map((r) => r.trim()) : undefined,
        billingExportTable: billingExportTable.trim() || undefined,
        stateBackend:
          stateBucket.trim() && stateObject.trim()
            ? { bucket: stateBucket.trim(), object: stateObject.trim() }
            : undefined,
      }),
    onSuccess: async (integration) => {
      if (integration.status === 'ERROR') {
        setError(
          integration.lastError ??
            'Could not federate into the project. Check the setup and retry.',
        );
        return;
      }
      // Opt-in Kubernetes (GKE) pod-level discovery, mirroring domain settings.
      // Best-effort: never block the connection if enabling it hiccups.
      if (includeK8s) {
        try {
          await setKubernetesAccess(envId, integration.id, {
            enabled: true,
            autoProvision: k8sAutoProvision,
          });
        } catch {
          /* best-effort */
        }
      }
      onConnected();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Failed to connect'),
  });

  const testState = useMutation({
    mutationFn: () =>
      testGcpStateBackend(envId, {
        projectId: projectId.trim(),
        serviceAccountEmail: serviceAccountEmail.trim(),
        workloadIdentityProvider: workloadIdentityProvider.trim(),
        stateBackend: { bucket: stateBucket.trim(), object: stateObject.trim() },
      }),
  });

  const copyScript = () => {
    if (!prep.data) return;
    void navigator.clipboard.writeText(prep.data.setupScript).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const ready =
    !!prep.data &&
    !!projectId.trim() &&
    !!serviceAccountEmail.trim() &&
    !!workloadIdentityProvider.trim();

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <Card
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h2 className="font-display text-xl font-bold">Connect GCP</h2>
        <p className="mt-2 text-sm text-muted">
          Run one script in your GCP project to create a read-only viewer that Lizrd can impersonate
          — keyless, via Workload Identity Federation. You stay in control and can revoke it any
          time.
        </p>

        <ul className="mt-4 space-y-2 text-sm">
          <li className="flex gap-2">
            <Lock className="h-4 w-4" aria-hidden />
            <span>
              <strong className="text-bone">Read-only.</strong> Lizrd only describes your resources
              and reads metrics/cost — it never modifies your infrastructure.
            </span>
          </li>
          <li className="flex gap-2">
            <KeyRound className="h-4 w-4" aria-hidden />
            <span>
              <strong className="text-bone">No keys, ever.</strong> Access is via Workload Identity
              Federation — Lizrd's own cloud identity is federated to impersonate your viewer
              service account; short-lived tokens are minted on demand. Nothing is downloaded or
              stored.
            </span>
          </li>
          <li className="flex gap-2">
            <span aria-hidden>↩️</span>
            <span>
              <strong className="text-bone">Revoke any time.</strong> Delete the Workload Identity
              pool or the viewer service account and access ends immediately.
            </span>
          </li>
        </ul>

        {prep.isError && (
          <Alert variant="danger" className="mt-4">
            GCP onboarding isn’t configured on this server.
          </Alert>
        )}

        {prep.data && (
          <div className="mt-5 space-y-4">
            <ol className="list-decimal space-y-3 pl-5 text-sm">
              <li>
                In your GCP project’s{' '}
                <a
                  href="https://console.cloud.google.com/cloudshell"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-iris underline"
                >
                  Cloud Shell
                </a>
                , run this script — it enables the required APIs, then creates the viewer service
                account + Workload Identity pool (trusting only Lizrd’s principal{' '}
                <code className="rounded-sm bg-surface-2 px-1">{prep.data.lizrdAwsPrincipal}</code>
                ). It’s safe to paste directly.
                <div className="mt-2 rounded-sm border border-border bg-surface-2/40">
                  <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
                    <span className="text-xs text-muted">setup.sh</span>
                    <Button variant="outline" size="sm" onClick={copyScript}>
                      {copied ? 'Copied ✓' : 'Copy'}
                    </Button>
                  </div>
                  <pre className="max-h-48 overflow-auto px-3 py-2 text-xs leading-relaxed text-bone">
                    {prep.data.setupScript}
                  </pre>
                </div>
              </li>
              <li>
                Paste the values the script prints, plus your project id.
                <div className="mt-2 space-y-2">
                  <label className="block">
                    <span className="mb-1 block text-xs text-muted">Project id</span>
                    <Input
                      placeholder="my-project"
                      value={projectId}
                      onChange={(e) => setProjectId(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-muted">Service account email</span>
                    <Input
                      placeholder="lizrd-viewer@my-project.iam.gserviceaccount.com"
                      value={serviceAccountEmail}
                      onChange={(e) => setServiceAccountEmail(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-muted">
                      Workload Identity provider
                    </span>
                    <Input
                      placeholder="projects/123/locations/global/workloadIdentityPools/lizrd-pool/providers/lizrd-aws"
                      value={workloadIdentityProvider}
                      onChange={(e) => setWorkloadIdentityProvider(e.target.value)}
                    />
                  </label>
                </div>
              </li>
            </ol>

            <div className="rounded-md border border-hairline bg-surface-2/40 p-3">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" aria-hidden />
                <span className="text-sm font-semibold text-bone">
                  Billing — required for billed cost
                </span>
              </div>
              <p className="mt-1 text-xs leading-relaxed text-muted">
                GCP exposes billed cost <strong className="text-bone">only</strong> through a
                BigQuery billing export — there’s no cost API like AWS or Azure. Paste your export
                table so Lizrd reads your actual spend and can bill accurately. Without it, GCP cost
                shows as <strong className="text-bone">estimates</strong> and billing can’t be
                finalized. The setup script above provisions the project side and prints the exact
                table name (as <code>billingExportTable</code>) plus the one Console step to enable
                the export.
              </p>
              <label className="mt-2 block">
                <span className="mb-1 block text-xs text-muted">BigQuery billing-export table</span>
                <Input
                  placeholder="project.dataset.gcp_billing_export_v1_XXXXXX"
                  value={billingExportTable}
                  onChange={(e) => setBillingExportTable(e.target.value)}
                />
              </label>
              <p className="mt-1 text-[0.7rem] text-muted">
                No export yet? You can connect now and add this later from the integration’s
                settings — cost stays estimated until you do.
              </p>
            </div>

            <details className="rounded-sm border border-border bg-surface-2/40 px-3 py-2 text-sm">
              <summary className="cursor-pointer font-semibold text-bone">
                Advanced (optional): regions, Terraform state
              </summary>
              <div className="mt-3 space-y-2">
                <label className="block">
                  <span className="mb-1 block text-xs text-muted">
                    Regions (comma-separated — blank scans all)
                  </span>
                  <Input
                    placeholder="us-central1, europe-west1"
                    value={regions}
                    onChange={(e) => setRegions(e.target.value)}
                  />
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="mb-1 block text-xs text-muted">Terraform state bucket</span>
                    <Input
                      placeholder="my-tfstate-bucket"
                      value={stateBucket}
                      onChange={(e) => setStateBucket(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-muted">…object path</span>
                    <Input
                      placeholder="env/prod/terraform.tfstate"
                      value={stateObject}
                      onChange={(e) => setStateObject(e.target.value)}
                    />
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={
                      !ready || !stateBucket.trim() || !stateObject.trim() || testState.isPending
                    }
                    onClick={() => testState.mutate()}
                  >
                    {testState.isPending ? 'Testing…' : 'Test read'}
                  </Button>
                  {testState.data &&
                    (testState.data.ok ? (
                      <span className="text-xs text-iris">
                        ✓ Read {testState.data.resourceCount} resources from state
                      </span>
                    ) : (
                      <span className="text-xs text-danger">{testState.data.message}</span>
                    ))}
                  {testState.isError && (
                    <span className="text-xs text-danger">Test failed — check the values.</span>
                  )}
                </div>
              </div>
            </details>

            <div className="rounded-md border border-iris/30 bg-iris-dim/20 p-4">
              <p className="text-sm font-semibold text-bone">Deeper optimization (recommended)</p>
              <div className="mt-3 flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-bone">Include Kubernetes workloads</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-muted">
                    If you run Kubernetes (GKE), Lizrd can surface deep, in-cluster optimizations —
                    right-size pods and spot idle workloads — but only if you let it read and sync
                    your cluster data. Read-only, like everything else.
                  </p>
                </div>
                <Toggle
                  checked={includeK8s}
                  onChange={setIncludeK8s}
                  label="Include Kubernetes workloads"
                />
              </div>
              {includeK8s && (
                <div className="mt-3 flex items-start justify-between gap-3 border-t border-hairline pt-3">
                  <div>
                    <p className="text-sm font-medium text-bone">
                      Set up cluster access automatically
                    </p>
                    <p className="mt-0.5 text-xs leading-relaxed text-muted">
                      {k8sAutoProvision ? (
                        <>
                          The setup script above grants the viewer service account{' '}
                          <code>roles/container.viewer</code>, which GKE maps to read-only
                          Kubernetes access — nothing else to do.
                        </>
                      ) : (
                        <>
                          You’ll grant the read-only access yourself (a{' '}
                          <code>ClusterRoleBinding</code> for the viewer SA). The integration’s
                          settings show the exact command — discovery reads once it’s applied.
                        </>
                      )}
                    </p>
                  </div>
                  <Toggle
                    checked={k8sAutoProvision}
                    onChange={setK8sAutoProvision}
                    label="Set up cluster access automatically"
                  />
                </div>
              )}
              <p className="mt-3 border-t border-hairline pt-3 text-xs leading-relaxed text-muted">
                <span className="font-medium text-bone">Infrastructure as Code.</span> Lizrd reads
                your Terraform/IaC from your connected code repository to tie each recommendation to
                the exact file to change — point it at your Terraform state (above) for exact
                code↔cloud correlation.
              </p>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!ready || connect.isPending}
            onClick={() => {
              setError(null);
              connect.mutate();
            }}
          >
            {connect.isPending ? 'Connecting…' : 'Connect'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
